import { all, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  actionRequest,
  completeRequest,
  dialogueRequest,
  moduleRequest,
  verificationRequest,
} from 'core/services/product';
import { selectRegister } from 'core/selectors/product/register';
import { IRegisterReducer, next } from 'core/slices/register';
import { IPlayerResponse, IResponseQueueItem } from 'types/action/player';
import { loadModule } from '../module/load';
import { IToken } from 'core/slices/session';
import { selectToken } from 'core/selectors/product/token';
import { IModuleAssign } from 'types/domain/module';
import { ICourseNodeDAO } from 'types/domain/course';
import { updatePageIds, waitForPageTransition } from './page';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { startModule } from '../module/start';
import { queueItem } from 'core/slices/queue';
import { updateState } from 'core/actions/product/state';

export function* handlePlayerResponse(playerResponse: IPlayerResponse) {
  const { queue, courseProgressId } = playerResponse;

  if (!queue.length) return;

  yield waitForPageTransition();

  const token: IToken = yield select(selectToken);

  yield all(
    queue.map(item =>
      processQueueItem(item, courseProgressId || token.courseProgressId, token.token || playerResponse.token),
    ),
  );

  const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);
  if (!actionId) yield updatePageIds();

  const register: IRegisterReducer = yield select(selectRegister);
  if (register.isNext) yield put(yield call(next, { culprit: 'handlePlayerResponse' }));
}

function* processQueueItem(item: IResponseQueueItem, courseProgressId: number, token: string) {
  if (item.module) yield prepareModule(item.module, courseProgressId, item.courseNodeId);
  else if (item.courseNode) yield registerDialogue(item.courseNode, courseProgressId, token);
  else if (item.courseNodeMenu) yield registerDialogue(item.courseNodeMenu, courseProgressId, token);
}

function* registerDialogue(cn: ICourseNodeDAO, courseProgressId: number, token: string) {
  yield put(yield call(queueItem, { courseNodeId: cn.id, courseProgressId, courseNodeType: cn.type }));
  const feed: IActionFeed = yield select(selectActionFeed);
  const courseNodeId = yield select(state => state.state[EntityTypes.COURSE_NODE]);
  const current = courseNodeId
    ? feed.feed.find(item => item.courseNodeId === cn.id) || feed.feed[feed.index]
    : feed.feed[feed.index];

  if (current && current.courseNodeId !== courseNodeId)
    yield put(yield call(updateState, { type: EntityTypes.COURSE_NODE, id: current.courseNodeId }));
}

function* prepareModule(module: IModuleAssign, courseProgressId: number, courseNodeId: number) {
  yield put(
    yield call(queueItem, {
      courseProgressId,
      courseNodeId,
      moduleId: module.id,
      sessionAttemptId: module.sessionAttemptId,
    }),
  );

  let loading = yield loadModule(module.id);

  while (loading) {
    const action = yield take(moduleRequest.fulfilled);
    loading = action.payload.id !== module.id;
  }

  const feed: IActionFeed = yield select(selectActionFeed);
  const actionId = yield select(state => state.state[EntityTypes.ACTION]);
  const current = actionId
    ? feed.feed.find(item => item.actionId === actionId) || feed.feed[feed.index]
    : feed.feed[feed.index];

  if (current && current.moduleId === module.id)
    yield call(startModule, module.sessionAttemptId, module.id, current.actionId);
}

function* playerResponseSaga({ payload, meta }) {
  yield handlePlayerResponse(payload);
}

function* watchResponses() {
  yield takeEvery(
    [actionRequest.fulfilled, verificationRequest.fulfilled, dialogueRequest.fulfilled, completeRequest.fulfilled],
    playerResponseSaga,
  );
}

export function* responseSaga() {
  yield all([fork(watchResponses)]);
}
